import { FC, useState, useEffect } from 'react';
import { TourProvider } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import { useGlobalTour } from '../contexts/TourContext';
import { useTranslatedSteps } from './stepsTour';
import {
  Box,
  Alert,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  // Avatar,
} from '@mui/material';
// import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import { onboarding } from '../services/api';
import { useTranslation } from '../contexts/TranslationContext';
import { enqueueSnackbar } from 'notistack';
import { phoneNumberValidation, toPersianDigits } from '../utils/validation';
import VideoProvider from '../components/VideoProvider';
import { videoUrls, videoTitles } from '../utils/videoUrls';

interface ProfileForm {
  fullName: string;
  phoneNumber: string;
  position: string;
  email: string;
}
let flag = false;

const normalizeProfileValue = (value: string | null | undefined) => value ?? '';

export const ProfileContent: FC = () => {
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  const [formData, setFormData] = useState<ProfileForm>({
    fullName: '',
    phoneNumber: '',
    position: '',
    email: '',
  });
  const [editFormData, setEditFormData] = useState<ProfileForm>({
    fullName: '',
    phoneNumber: '',
    position: '',
    email: '',
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [profileImage, setProfileImage] = useState<string>('/images/profile.svg');
  const [newProfileImage, setNewProfileImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string>('');
  const { t } = useTranslation();

  // Register tour function with global tour context
  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await onboarding.getPersonalInfo();
        setFormData({
          fullName: normalizeProfileValue(response.data.full_name),
          phoneNumber: normalizeProfileValue(response.data.phone_number),
          position: normalizeProfileValue(response.data.position),
          email: normalizeProfileValue(response.data.email),
        });
      } catch (err: any) {
        if (err.response?.status === 404) {
          setNotFound(true);
          enqueueSnackbar(t('profile.error.notFound'), { 
            variant: 'error',
            autoHideDuration: 5000,
            style: {
              padding: '10px',
              marginTop: '20px',
            },
          });
        } else if (err.response?.status === 400) {
          enqueueSnackbar(t('profile.error.incomplete'), { 
            variant: 'error',
            autoHideDuration: 5000,
            style: {
              padding: '10px',
              marginTop: '20px',
            },
          });
        } else {
          if (!flag) {
            enqueueSnackbar(t('profile.error.sessionExpired'), { 
              variant: 'error',
              autoHideDuration: 5000,
              style: {
                padding: '10px',
                marginTop: '20px',
              },
            });
            flag = true;
          }
          setTimeout(() => {
            localStorage.removeItem('token');
            window.location.href = '/auth/auth';
          }, 4000);
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const handleOpenModal = () => {
    setEditFormData({ ...formData });
    setPreviewImage(profileImage);
    setNewProfileImage(null);
    setPhoneError('');
    setEmailError('');
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setPhoneError('');
    setEmailError('');
    setPreviewImage('');
    setNewProfileImage(null);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'phoneNumber') {
      setPhoneError('');
    }
    if (name === 'email') {
      setEmailError('');
    }
  };

  // const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (e.target.files && e.target.files[0]) {
  //     const file = e.target.files[0];
  //     setNewProfileImage(file);
      
  //     // Create preview
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       setPreviewImage(reader.result as string);
  //     };
  //     reader.readAsDataURL(file);
  //   }
  // };

  const handleSaveProfile = async () => {
    // Validate phone number
    const phoneValidation = phoneNumberValidation(editFormData.phoneNumber);
    if (!phoneValidation.isValid) {
      setPhoneError(t('profile.error.phone_number_invalid'));
      return;
    }

    try {
      setLoading(true);
      setPhoneError('');
      setEmailError('');
      await onboarding.savePersonalInfo({
        full_name: normalizeProfileValue(editFormData.fullName).trim(),
        phone_number: phoneValidation.normalizedPhone || normalizeProfileValue(editFormData.phoneNumber).trim(),
        position: normalizeProfileValue(editFormData.position).trim(),
        email: normalizeProfileValue(editFormData.email).trim(),
      });

      // Update local state
      setFormData({ ...editFormData });
      
      // If there's a new profile image, update it
      if (newProfileImage) {
        setProfileImage(previewImage);
        // Here you would typically upload the image to the backend
        // await uploadProfileImage(newProfileImage);
      }

      enqueueSnackbar(t('profile.success'), { 
        variant: 'success',
        autoHideDuration: 5000,
        style: {
          padding: '10px',
          marginTop: '20px',
        },
      });
      
      handleCloseModal();
    } catch (err: any) {
      if (err.response?.status === 400) {
        let error_mes = "";
        if (err.response?.data?.detail.includes("Email")) {
          error_mes = t('profile.error.email_error_already_register');
        } else if (err.response?.data?.detail.includes("Phone number")) {
          error_mes = t('profile.error.phone_error_already_register');
        }
        enqueueSnackbar(error_mes || t('profile.error.incomplete'), { 
          variant: 'error',
          autoHideDuration: 5000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
      } else if (err.response?.status === 500) {
        enqueueSnackbar(t('profile.error.saveError'), { 
          variant: 'error',
          autoHideDuration: 5000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
      } else {
        let error_mes = "";
        if (err.response?.data?.detail?.[0]?.msg === "String should match pattern '^\\+?[0-9]+$'") {
          error_mes = t('profile.error.phone_pattern_error');
        } else if (err.response?.data?.detail?.[0]?.msg === "String should have at least 2 characters") {
          error_mes = t('profile.error.string_pattern_error');
        }
        enqueueSnackbar(error_mes || t('profile.error.saveError'), { 
          variant: 'error',
          autoHideDuration: 5000,
          style: {
            padding: '10px',
            marginTop: '20px',
          },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Alert severity="warning">
            {t('profile.error.notFound')}
          </Alert>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="first-step">
      <Box sx={{borderRadius: '10px', overflow:'hidden' , position:'relative' }}>
        <Box sx={{height: '110px', backgroundImage: 'url(/images/backProfile2.jpg)', backgroundSize: 'cover'}} className='back_profile_image'>
        </Box>
        <Box sx={{position:'absolute', top: '30px', left:'40px' , overflow:'hidden', zIndex: 1000, display:'flex', alignItems:'center', gap:'30px'}} className='profile_box_profile'>
          <img src={profileImage} alt="" style={{height:'120px' , width:'120px' , borderRadius: '50%', backgroundColor: 'white'}} className='profile_image' />
          <p style={{fontSize: '24px', fontWeight: 'bold', textAlign: 'center', paddingBottom:'25px', color: 'white'}} className='text_profile_name'>{formData.fullName}</p>
        </Box>
        <Box sx={{height:'100%' , p:{xs:3, md:4}, pt:{xs:4, md:5}, display:'flex', flexDirection:'column'}}>
          {/* Phone Number */}
          <Box sx={{ mb: {xs:1, md:3} }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('profile.phoneNumber')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0',
              direction: 'ltr',
              textAlign: 'left',
              overflowX: 'auto'
            }}>
              <Typography
                variant="body1"
                dir="ltr"
                sx={{ direction: 'ltr', unicodeBidi: 'isolate', textAlign: 'left' }}
              >
                {formData.phoneNumber
                  ? `\u2066${toPersianDigits(formData.phoneNumber)}\u2069`
                  : '-'}
              </Typography>
            </Box>
          </Box>

          {/* Email */}
          <Box sx={{ mb: {xs:1, md:3} }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('profile.email')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0',
              direction: 'ltr',
              textAlign: 'left',
              overflowX: 'auto'
            }}>
              <Typography variant="body1">
                {formData.email || '-'}
              </Typography>
            </Box>
          </Box>

          {/* Position */}
          <Box sx={{ mb: {xs:1, md:3} }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('profile.position')}
            </Typography>
            <Box sx={{ 
              p: 2, 
              borderRadius: '8px', 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0',
              overflowX: 'auto'
            }}>
              <Typography variant="body1">
                {formData.position || '-'}
              </Typography>
            </Box>
          </Box>

            {/* Edit Profile Button */}
          <Box sx={{alignSelf:'end' , marginTop:{xs:1, md:2}}}>
            <Button
              variant="contained"
              onClick={handleOpenModal}
              className="profile-edit-button"
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '0.875rem',
                px: { xs: 2, sm: 3 },
                py: { xs: 0.9, sm: 1 },
                '@media (max-width: 600px)': {
                  fontSize: '0.85rem !important',
                  width: 'auto !important',
                  minWidth: 'auto !important',
                },
              }}
            >
              {t('profile.edit_profile')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Edit Profile Modal */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            {t('profile.edit_modal_title')}
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Profile Image Section */}
          {/* <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={previewImage}
                alt="Profile"
                sx={{ 
                  width: 120, 
                  height: 120,
                  border: '4px solid #f5f5f5'
                }}
              />
              <IconButton
                component="label"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  width: 40,
                  height: 40
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 20 }} />
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </IconButton>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              برای تغییر عکس پروفایل کلیک کنید
            </Typography>
          </Box> */}
          {/* Form Fields */}

          <TextField
            fullWidth
            label={t('profile.fullName')}
            name="fullName"
            value={editFormData.fullName}
            onChange={handleEditChange}
            margin="normal"
            required
            helperText={t('profile.fullNameHelper')}
          />

          <TextField
            fullWidth
            label={t('profile.phoneNumber')}
            name="phoneNumber"
            value={editFormData.phoneNumber}
            onChange={handleEditChange}
            margin="normal"
            required
            dir="ltr"
            placeholder="+989123456789"
            helperText={phoneError || t('profile.phoneNumberHelper')}
            error={!!phoneError}
          />

          <TextField
            fullWidth
            label={t('profile.email')}
            name="email"
            value={editFormData.email}
            onChange={handleEditChange}
            margin="normal"
            required
            dir="ltr"
            placeholder="example@gmail.com"
            helperText={emailError || t('profile.emailHelper')}
            error={!!emailError}
          />

          <TextField
            fullWidth
            label={t('profile.position')}
            name="position"
            value={editFormData.position}
            onChange={handleEditChange}
            margin="normal"
            required
            helperText={t('profile.positionHelper')}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleCloseModal} 
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              textTransform: 'none',
              minWidth: 100
            }}
          >
            {t('profile.cancel')}
          </Button>
          <Button 
            onClick={handleSaveProfile}
            variant="contained"
            disabled={
              loading ||
              !normalizeProfileValue(editFormData.fullName).trim() ||
              !normalizeProfileValue(editFormData.phoneNumber).trim() ||
              !normalizeProfileValue(editFormData.position).trim()
            }
            sx={{ 
              borderRadius: '8px',
              textTransform: 'none',
              minWidth: 100
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('profile.save_modal_profile')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Profile: FC = () => {
  return (
    <TourProvider steps={useTranslatedSteps().steps_profile}>
      <VideoProvider videoUrl={videoUrls.profile} videoTitle={videoTitles.profile}>
        <ProfileContent />
      </VideoProvider>
    </TourProvider>
  );
};

export default Profile; 